import { useState } from 'react';
import axios from 'axios';
import { Mail, Lock, LogIn, ArrowLeft } from 'lucide-react';
import Spinner from '../components/Spinner';
import { MapContainer, TileLayer } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

function Login() {
  const [form, setForm] = useState({ email: '', password: '' });
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(form.email)) {
        setMessage('❌ Voer een geldig e-mailadres in');
        setLoading(false);
        return;
      }
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/login`, form);
      if (res.data.user?.role !== 'admin') {
        setMessage('❌ Inloggegevens kloppen niet');
        setLoading(false);
        return;
      }
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      setMessage('✅ Ingelogd! Doorsturen...');
      setTimeout(() => window.location.href = '/admin', 1500);
    } catch (err) {
      setMessage('❌ Inloggegevens kloppen niet');
    }
    setLoading(false);
  };

  const isError = message.startsWith('❌') || (!message.startsWith('✅') && message.length > 0);

  return (
    <div style={styles.container}>
      <div style={styles.mapBg}>
        <MapContainer center={[52.3676, 4.9041]} zoom={12} zoomControl={false} dragging={false} touchZoom={false} scrollWheelZoom={false} doubleClickZoom={false} keyboard={false} attributionControl={false} style={{ width: '100%', height: '100%' }}>
          <TileLayer url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" />
        </MapContainer>
      </div>
      <div style={styles.mapOverlay} />
      <div style={styles.card}>

        {/* Header */}
        <div style={styles.header}>
          <button onClick={() => window.history.back()} style={{ position: 'absolute', top: '16px', left: '16px', background: 'white', border: 'none', borderRadius: '50%', width: '34px', height: '34px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
            <ArrowLeft size={16} color="#1d4ed8" />
          </button>
          <img src="/images/Beeldmerk.svg" alt="SpotjeWcPotje" style={styles.logoImg} />
          <h1 style={styles.title}>SpotjeWcPotje</h1>
          <p style={styles.subtitle}>Log in op je account</p>
        </div>

        {/* Formulier */}
        <div style={styles.form}>
          <div style={styles.inputWrapper}>
            <Mail size={16} color="#aaa" style={styles.inputIcon} />
            <input
              style={styles.input}
              name="email"
              type="email"
              placeholder="E-mailadres"
              onChange={handleChange}
              autoComplete="email"
            />
          </div>

          <div style={styles.inputWrapper}>
            <Lock size={16} color="#aaa" style={styles.inputIcon} />
            <input
              style={styles.input}
              name="password"
              placeholder="Wachtwoord"
              type="password"
              onChange={handleChange}
              autoComplete="current-password"
            />
          </div>

          <button
            style={{ ...styles.button, opacity: loading ? 0.7 : 1 }}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? <Spinner /> : (
              <>
                <LogIn size={16} />
                Inloggen
              </>
            )}
          </button>

          <a href="/wachtwoord-vergeten" style={styles.forgotLink}>Wachtwoord vergeten?</a>

          {message && (
            <div style={{ ...styles.messageBanner, backgroundColor: isError ? '#fef2f2' : '#f0fdf4', color: isError ? '#dc2626' : '#16a34a', borderColor: isError ? '#fecaca' : '#bbf7d0' }}>
              {message.replace(/^[✅❌]\s*/, '')}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

const styles = {
  container: { position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', minHeight: '100vh', padding: '20px', overflowX: 'hidden' },
  mapBg: { position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none' },
  mapOverlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(10, 20, 45, 0.78)', zIndex: 1 },
  card: { position: 'relative', zIndex: 2, backgroundColor: 'white', borderRadius: '20px', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', width: '100%', maxWidth: '380px', overflow: 'hidden' },
  header: { position: 'relative', background: 'linear-gradient(135deg, #1d4ed8, #3b82f6)', padding: '36px 32px 28px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' },
  logoImg: { width: '56px', height: '56px', objectFit: 'contain' },
  title: { margin: 0, fontSize: '22px', fontWeight: '800', color: 'white' },
  subtitle: { margin: 0, fontSize: '14px', color: 'rgba(255,255,255,0.8)' },
  form: { padding: '28px 28px 20px', display: 'flex', flexDirection: 'column', gap: '12px' },
  inputWrapper: { position: 'relative', display: 'flex', alignItems: 'center' },
  inputIcon: { position: 'absolute', left: '12px', pointerEvents: 'none' },
  input: { padding: '13px 12px 13px 38px', borderRadius: '10px', border: '1.5px solid #e5e7eb', fontSize: '14px', fontFamily: 'inherit', width: '100%', boxSizing: 'border-box', outline: 'none', transition: 'border-color 0.2s' },
  button: { marginTop: '4px', padding: '14px', borderRadius: '10px', backgroundColor: '#3b82f6', color: 'white', border: 'none', fontSize: '15px', cursor: 'pointer', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' },
  messageBanner: { padding: '12px 14px', borderRadius: '10px', fontSize: '13px', fontWeight: '500', border: '1px solid', textAlign: 'center' },
  footer: { borderTop: '1px solid #f3f4f6', padding: '18px 28px', textAlign: 'center' },
  footerText: { margin: 0, fontSize: '14px', color: '#888' },
  footerLink: { color: '#3b82f6', fontWeight: '600', textDecoration: 'none' },
  forgotLink: { textAlign: 'center', fontSize: '12px', color: '#bbb', textDecoration: 'none', display: 'block' },
};

export default Login;
